import Cookies from 'js-cookie';
import * as log from '../util/log';
import { storableError } from '../util/errors';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import { createUserWithIdp, sendWelcomeEmail } from '../util/api';
import { message } from 'antd';
import appSettings from '../config/settings';
import { userTypeOf } from '../util/userType';
import { sessionExpiredLoginUrl } from '../util/sessionExpiredLogin';

// Session expiry in milliseconds - MUST match server value in server/api-util/sdk.js
const SESSION_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours
const SESSION_CHECK_INTERVAL_MS = 60 * 1000; // Check every 1 minute

// Store interval ID so we can clear it on logout
let sessionCheckIntervalId = null;

// Base36 character set for Customer ID generation (UPPERCASE)
const BASE36 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Base36 encode a hex string
 * @param {string} hex - The hex string to encode
 * @returns {string} - Base36 encoded string (UPPERCASE)
 */
function base36Encode(hex) {
  let num = BigInt('0x' + hex);
  let encoded = '';
  while (num > 0n) {
    encoded = BASE36[Number(num % 36n)] + encoded;
    num /= 36n;
  }
  return encoded;
}

/**
 * Generate a unique Customer ID from a Sharetribe GUID
 * Format: CUS-<BASE36_VALUE> (10 characters)
 * @param {string} guid - The Sharetribe User GUID
 * @param {number} length - Length of the encoded portion (default: 10)
 * @returns {string} - Customer ID (e.g., CUS-9F3K7A2MQL)
 */
function generateCustomerId(guid, length = 10) {
  if (!guid) throw new Error('GUID is required');
  const hex = guid.replace(/-/g, '').toLowerCase();
  if (hex.length !== 32) throw new Error('Invalid GUID');
  const encoded = base36Encode(hex);
  const shortCode = encoded.substring(0, length);
  return `CUS-${shortCode}`;
}

const authenticated = authInfo => authInfo?.isAnonymous === false;
const loggedInAs = authInfo => authInfo?.isLoggedInAs === true;

const hasRemovedOrganizationAccess = currentUser => {
  const publicData = currentUser?.attributes?.profile?.publicData || {};
  return publicData?.removedFromOrganization === true;
};

const handleRestrictedLoginState = (dispatch, currentUser) => {
  if (hasRemovedOrganizationAccess(currentUser)) {
    dispatch(logout());
    message.error('Your organization access has been removed. Please contact your administrator.');
    return true;
  }

  const isActive = currentUser?.attributes?.profile?.publicData?.isActive;
  if (isActive == false) {
    dispatch(logout());
    message.error('This account has been deactivated. Please contact the Admin.');
    return true;
  }

  return false;
};

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/auth/LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/auth/CONFIRM_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // is marketplace operator logged in as a marketplace user
  isLoggedInAs: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        isLoggedInAs: loggedInAs(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return {
        ...state,
        logoutInProgress: false,
        isAuthenticated: false,
        isLoggedInAs: false,
        authScopes: [],
      };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress, confirmInProgress } = state.auth;
  return loginInProgress || logoutInProgress || signupInProgress || confirmInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });

export const userLogout = () => ({ type: USER_LOGOUT });

// ================ Thunks ================ //

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

export const login = (username, password) => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(loginRequest());

  // Note that the thunk does not reject when the login fails, it
  // just dispatches the login error action.
  return sdk
    .login({ username, password })
    .then(() => dispatch(fetchCurrentUser({ afterLogin: true })))
    .then(() => {
      dispatch(loginSuccess());
      const state = getState();
      const currentUser = state.user?.currentUser;

      if (handleRestrictedLoginState(dispatch, currentUser)) {
        return;
      }

      // Start session timeout checker after successful login
      dispatch(startSessionTimeoutChecker());
    })
    .catch(e => dispatch(loginError(storableError(e))));
};

export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());

  // Stop the session timeout checker
  if (sessionCheckIntervalId) {
    clearInterval(sessionCheckIntervalId);
    sessionCheckIntervalId = null;
  }

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  return sdk
    .logout()
    .then(() => {
      // The order of the dispatched actions
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem("currentStep");
      }

      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      log.clearUserId();
      dispatch(userLogout());
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const signup = params => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());
  // Note: params are already structured on AuthenticationPage (handleSubmitSignup)

  // We must login the user if signup succeeds since the API doesn't
  // do that automatically.
  let toSendParams = params;
  toSendParams.displayName = params?.firstName + ' ' + params?.lastName;

  return sdk.currentUser
    .create(toSendParams)
    .then(() => {
      dispatch(signupSuccess());
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('selectUserType');
      }
    })
    .then(() => dispatch(login(params.email, params.password)))
    .then(() => {
      // After login, generate and save unique Customer ID
      const state = getState();
      const currentUserId = state.user?.currentUser?.id?.uuid;
      if (currentUserId) {
        const customerId = generateCustomerId(currentUserId);
        return sdk.currentUser.updateProfile({
          publicData: {
            customerId: customerId,
          },
        });
      }
    })
    .then(() => {
      // Send welcome email based on user type
      const state = getState();
      const currentUser = state.user?.currentUser;
      const userType = userTypeOf(currentUser);
      const firstName = currentUser?.attributes?.profile?.firstName || params.firstName;
      const email = currentUser?.attributes?.email || params.email;
      const userId = currentUser?.id?.uuid;

      if (userType && email && firstName) {
        // Send welcome email (fire and forget - don't block signup on email failure)
        sendWelcomeEmail({ userType, email, firstName, userId })
          .catch(e => {
            console.error('Failed to send welcome email:', e);
          });
      }
    })
    .catch(e => {
      dispatch(signupError(storableError(e)));
      log.error(e, 'signup-failed', {
        email: params.email,
        firstName: params.firstName,
        lastName: params.lastName,
      });
    });
};

export const signupWithIdp = params => (dispatch, getState, sdk) => {
  dispatch(confirmRequest());
  return createUserWithIdp(params)
    .then(() => {
      dispatch(confirmSuccess());
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('selectUserType');
        localStorage.removeItem('inviteRole');
        localStorage.removeItem('inviteListingId');
      }
    })
    .then(() => dispatch(fetchCurrentUser()))
    .then(() => {
      const state = getState();
      const currentUser = state.user?.currentUser;

      if (handleRestrictedLoginState(dispatch, currentUser)) {
        return;
      }

      // Start session timeout checker after successful IDP login
      dispatch(startSessionTimeoutChecker());
    })
    .catch(e => {
      log.error(e, 'create-user-with-idp-failed', { params });
      return dispatch(confirmError(storableError(e)));
    });
};

// Check if the session has expired based on the session-start cookie
const isSessionExpired = () => {
  const clientId = appSettings.sdk.clientId;
  const sessionCookieName = `st-${clientId}-session-start`;
  const sessionStart = Cookies.get(sessionCookieName);

  if (!sessionStart) {
    return false; // No session start cookie means no active session
  }

  const elapsed = Date.now() - parseInt(sessionStart, 10);
  return elapsed > SESSION_EXPIRY_MS;
};

// Stop the session timeout checker
export const stopSessionTimeoutChecker = () => {
  if (sessionCheckIntervalId) {
    clearInterval(sessionCheckIntervalId);
    sessionCheckIntervalId = null;
  }
};

// Start the session timeout checker - call this after authInfo is loaded
export const startSessionTimeoutChecker = () => (dispatch, getState) => {
  // Clear any existing interval
  stopSessionTimeoutChecker();

  // Only start checker if user is authenticated
  const { isAuthenticated } = getState().auth;
  if (!isAuthenticated) {
    return;
  }

  sessionCheckIntervalId = setInterval(() => {
    const state = getState();
    const { isAuthenticated: stillAuthenticated, logoutInProgress } = state.auth;

    // If not authenticated or already logging out, stop checking
    if (!stillAuthenticated || logoutInProgress) {
      stopSessionTimeoutChecker();
      return;
    }

    // Check if session expired
    if (isSessionExpired()) {
      stopSessionTimeoutChecker();
      // Open the login page whatever the sign-out does: a sign-out that fails, or one refused
      // because another sign-in or sign-out is running, still leaves the user needing to sign in.
      // The login page carries the page they are on, so sign-in returns them there.
      Promise.resolve()
        .then(() => dispatch(logout()))
        .catch(() => null)
        .then(() => {
          if (typeof window !== 'undefined') {
            window.location.href = sessionExpiredLoginUrl(window.location);
          }
        });
    }
  }, SESSION_CHECK_INTERVAL_MS);
};
